/**
 * "What YOU hear" - and a way to put your own file behind it.
 *
 * ONE FIELD, TWO RINGS. A call coming in and a call going out get the same
 * control because they are the same act: upload a file, hear it, save. The
 * `kind` prop is the whole difference and it decides exactly three things -
 * which endpoint stores the file, which words describe it, and what plays when
 * nothing has been uploaded.
 *
 * THAT LAST ONE IS NOT COSMETIC. Empty means different things on the two rings.
 * Incoming falls back to the trill this front end ships with, so there is
 * always something to play and the player is always drawn. Outgoing has no
 * default of ours at all - the Voice SDK's own sound stays switched on - so
 * with nothing uploaded there is genuinely nothing here to preview, and drawing
 * an empty player would invite somebody to press play on silence and conclude
 * the feature is broken.
 *
 * Deliberately simpler than the hold music field beside it, in two ways that
 * are worth naming because the two look similar enough to invite copying.
 *
 * NO URL BOX. Hold music has one because a track may already be hosted
 * somewhere Twilio can reach, and Twilio is a stranger fetching over the public
 * internet. A ring is loaded by this operator's own browser, from our own
 * origin, and both `ringSource.ts` and the server's `clampRingUrl` refuse
 * anything else - so a text field taking an address would offer a choice
 * neither end will honour.
 *
 * THE PLAYER LOADS THE VALUE DIRECTLY. Hold music cannot: its saved address
 * serves Twilio a looping TwiML document rather than audio. A ring's address is
 * the audio, so what is previewed here is exactly what will ring.
 *
 * Two presses, not one, exactly as hold music does it. Uploading stores the
 * file and fills in the address; Save is what makes the phone ring with it. The
 * gap between them is where the operator presses play and hears what they just
 * chose, and a single button doing both would put an unheard sound on the phone.
 */
import { useRef, useState } from "react";
import { AlertCircle, Bell, PhoneOutgoing, RefreshCw, Upload, X } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import {
  uploadRingback,
  uploadRingtone,
  type UploadedRingtone,
} from "../../../../lib/telephony";
import { Btn } from "../../../ui";
import { fmtBytes } from "../../../ui/format";
import { Field, Note } from "../panel/fields";

/**
 * What each failure the endpoint can report is called on screen.
 *
 * ⚠ THIS TABLE HAS TO COVER `ringtone.routes.ts` COMPLETELY. A code the route
 * can send and this cannot name is a dead end dressed as a retry.
 *
 * Shared by both rings and kept in the `ringtone` namespace on purpose: every
 * one of these is about the UPLOAD - the file was too big, the format is wrong,
 * the encoder is missing - and none of them is about which ring it was for. Two
 * copies would be two places to fix the same sentence.
 */
const ERROR_KEYS: Record<string, string> = {
  no_file: "settings.telephony.ringtone.errEmpty",
  file_too_large: "settings.telephony.ringtone.errTooLarge",
  unsupported_type: "settings.telephony.ringtone.errType",
  encoder_missing: "settings.telephony.ringtone.errEncoder",
  encode_failed: "settings.telephony.ringtone.errEncoder",
  upload_failed: "settings.telephony.ringtone.errFailed",
};

/** What the file dialog offers. The two formats the route accepts, nothing else. */
const ACCEPT = "audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav";

/** The ring the front end ships for incoming calls. See `incoming/ringSpec.ts`. */
const BUILT_IN_RING = "/ring.wav";

export type RingKind = "incoming" | "outgoing";

/**
 * Everything the two rings disagree about, in one table.
 *
 * A table rather than two components: the moment the difference is spread
 * across two files, one of them gets the next fix and the other does not.
 */
const RING: Record<
  RingKind,
  {
    keys: string;
    icon: typeof Bell;
    upload: (file: File) => Promise<UploadedRingtone>;
    /** What plays when nothing is chosen, or "" when there is nothing of ours. */
    fallback: string;
  }
> = {
  incoming: {
    keys: "settings.telephony.ringtone",
    icon: Bell,
    upload: (file) => uploadRingtone(file),
    fallback: BUILT_IN_RING,
  },
  outgoing: {
    keys: "settings.telephony.ringback",
    icon: PhoneOutgoing,
    upload: (file) => uploadRingback(file),
    fallback: "",
  },
};

export function RingtoneField({
  kind = "incoming",
  value,
  onChange,
}: {
  /** Which ring this field edits. Defaults to the one that existed first. */
  kind?: RingKind;
  /** May be undefined against an API older than this field. */
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  const t = useT();
  const spec = RING[kind];
  const Icon = spec.icon;
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The file uploaded in this form and not saved yet. Nothing on the server
  // reports it until the card is saved, so without this the line under a file
  // the operator just chose would describe the one it replaced.
  const [fresh, setFresh] = useState<UploadedRingtone | null>(null);

  // Defensive, and the reason is a real outage rather than a style: an API that
  // predates this field sends nothing for it, and a settings page that throws
  // on a missing key takes every OTHER setting on the card down with it. A
  // field that does not know its value renders as "not chosen".
  const chosen = (value ?? "").trim();
  const unsaved = fresh !== null && fresh.ringtoneUrl === chosen;
  // Empty is not "no ring" for the incoming one - it is the built-in one - so
  // there is always something to hear and a player that vanished would read as
  // the phone being mute. For the outgoing ring empty really is "not ours", and
  // there is nothing at this end to play.
  const preview = chosen || spec.fallback;

  async function handleFile(file: File) {
    setBusy(true);
    setError(null);
    try {
      const uploaded = await spec.upload(file);
      setFresh(uploaded);
      onChange(uploaded.ringtoneUrl);
    } catch (err) {
      const code = err instanceof ApiError ? err.message : "";
      setError(ERROR_KEYS[code] ?? "settings.telephony.ringtone.errFailed");
    } finally {
      setBusy(false);
      // Cleared so that choosing the SAME file again still fires a change: the
      // input keeps its value otherwise, and a failed upload could not be
      // retried without picking a different file first.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  return (
    /* A hairline block, matching the hold music field above it: the two are the
       same kind of thing and should not look like different mechanisms. */
    <div className="grid gap-3 rounded-[20px] border border-[var(--crm-hair)] p-4">
      <Field label={t(`${spec.keys}.label`)}>
        <div className="crm-sub flex items-center gap-1.5 text-[12px] leading-snug">
          <Icon className="size-3.5 shrink-0" />
          <span className="min-w-0 truncate">
            {chosen
              ? `${fresh?.label || t(`${spec.keys}.unnamed`)}${
                  fresh ? ` - ${fmtBytes(fresh.bytes)}` : ""
                }`
              : t(`${spec.keys}.builtIn`)}
          </span>
        </div>
      </Field>

      {/* Outside the Field on purpose: that one is a `label`, and a button
          inside a label has its clicks forwarded to the label's control. */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        <Btn tone="dark" onClick={() => fileInput.current?.click()} disabled={busy}>
          {busy ? <RefreshCw className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
          {busy
            ? t(`${spec.keys}.uploading`)
            : chosen
              ? t(`${spec.keys}.replace`)
              : t(`${spec.keys}.upload`)}
        </Btn>
        {/* Only offered when there is something to clear. Going back to the
            default ring is a real choice, not a mistake. */}
        {chosen ? (
          <Btn
            onClick={() => {
              onChange("");
              setFresh(null);
              setError(null);
            }}
            disabled={busy}
          >
            <X className="size-3.5" />
            {t(`${spec.keys}.clear`)}
          </Btn>
        ) : null}
      </div>

      <span className="crm-sub block text-[11.5px] leading-snug">{t(`${spec.keys}.hint`)}</span>

      {/* Hearing it is the only check that counts, and here the preview IS the
          ring: same address, same file. Nothing loads until somebody presses
          play, and nothing is drawn at all when there is no file of ours behind
          the setting.

          `color-scheme` is what darkens the browser's own player skin - nothing
          else in the app sets it, so without this the control stays a bright
          white pill on the dark card. */}
      {preview ? (
        <audio
          className="-mt-1 w-full max-w-[420px] [color-scheme:light] dark:[color-scheme:dark]"
          controls
          preload="none"
          src={preview}
        />
      ) : null}

      {unsaved ? <Note tone="warn">{t(`${spec.keys}.unsaved`)}</Note> : null}

      {error ? (
        <Note tone="bad" icon={<AlertCircle className="size-3.5" />} live>
          {t(error)}
        </Note>
      ) : null}
    </div>
  );
}
